import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useField } from 'react-final-form';
import { useLocation, useParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import cm from 'classnames';
import IMask from 'imask';
import { TextField } from 'ksnm-common-ui/lib/Form/InputFields';
import { SelectField } from 'ksnm-common-ui/lib/Form/SelectField';
import { createMask } from 'ksnm-common-ui/lib/MaskedInput';
import type { ReactElement } from 'react';

import { counterpartiesStore } from '@stores/dictionaryStores/counterpartiesStore/counterpartiesStore';
import { organizationsStore } from '@stores/dictionaryStores/organizationsStore';
import { organizationCardStore } from '@stores/dictionaryStores/organizationsStore/organizationCardStore';
import { organizationTypeStore } from '@stores/dictionaryStores/organizationTypeStore';
import { orgBusinessTypesStore } from '@stores/dictionaryStores/orgBusinessTypesStore';
import type { CompanyDto } from '@src/api';

import { OrganizationCardFormField } from '../types';

import styles from './OrganizationCardInfo.module.scss';

export function OrganizationCardInfoComponent({
  readOnly,
  counterparties,
}: {
  readOnly: boolean;
  counterparties: CompanyDto[];
}): ReactElement {
  const [filteredCompanies, setFilteredCompanies] = useState<CompanyDto[]>([]);

  const { fetchByParentCode } = counterpartiesStore;
  const { orgTypes } = organizationTypeStore;
  const { orgBusinessTypes } = orgBusinessTypesStore;
  const { organizations } = organizationsStore;
  const { isNew, organization } = organizationCardStore;

  const { state: routeState } = useLocation();

  const {
    input: { onChange: onChangeCompanyCode },
  } = useField(OrganizationCardFormField.COMPANY_CODE);

  const {
    input: { onChange: changeInn },
  } = useField(OrganizationCardFormField.INN);

  const {
    input: { onChange: changeKpp },
  } = useField(OrganizationCardFormField.KPP);

  const {
    input: { onChange: onChangeOrgName },
  } = useField(OrganizationCardFormField.NAME);

  const {
    input: { onChange: onChangeFullNameOrg },
  } = useField(OrganizationCardFormField.COMPANY_FULL_NAME);

  const {
    input: { onChange: onChangeOrgAdress },
  } = useField(OrganizationCardFormField.ADRESS);

  const {
    input: { onChange: onChangeBusinessType },
  } = useField(OrganizationCardFormField.BUSINESS_TYPE_CODE);

  const { code } = useParams<{ code: string }>();

  const orgInfo = useMemo(() => organizations?.find((org) => org.code === code), [code, organizations]);

  const businessTypeItems = useMemo(
    () =>
      orgBusinessTypes.map(({ businessTypeDescription, id }) => ({
        id,
        name: businessTypeDescription,
      })),
    [orgBusinessTypes],
  );

  useEffect(() => {
    setFilteredCompanies(
      counterparties
        .filter((el) =>
          orgInfo?.head
            ? el.inn && el.inn.length === 10 && el.code === orgInfo?.companyCode
            : el.inn && el.inn.length === 10,
        )
        .map((item) => ({ ...item, id: item.code, name: `${item.code} - ${item.name}` })),
    );
  }, [counterparties, orgInfo?.companyCode, orgInfo?.head]);

  useEffect(() => {
    const abortControl = new AbortController();
    (async () => {
      const orgCode = orgInfo?.parentCode;

      if (routeState?.organizationCode || orgCode) {
        const res = await fetchByParentCode(routeState?.organizationCode ?? orgCode, abortControl.signal);
        setFilteredCompanies(res.map((item) => ({ ...item, name: `${item.code} - ${item.name}` })));
      }
    })();

    return () => {
      abortControl.abort();
    };
  }, [fetchByParentCode, orgInfo?.parentCode, routeState?.organizationCode]);

  const onChangeCompany = useCallback(
    (value) => {
      const company = counterparties?.find((com) => com.code === value);
      onChangeCompanyCode(value);
      changeInn(company?.inn);
      changeKpp(company?.kpp);
      onChangeOrgName(company?.name);
      if (routeState?.head || organization?.head) {
        onChangeFullNameOrg(company?.fullName);
        onChangeOrgAdress(company?.legalAddress);
      }
    },
    [
      changeInn,
      changeKpp,
      counterparties,
      onChangeCompanyCode,
      onChangeFullNameOrg,
      onChangeOrgAdress,
      onChangeOrgName,
      organization?.head,
      routeState?.head,
    ],
  );

  const orgTypesForChildOrg = useMemo(
    () => orgTypes.filter((orgType) => !routeState?.head && !organization?.head && orgType.code !== 1),
    [orgTypes, organization?.head, routeState?.head],
  );

  const codeMask = useMemo(
    () =>
      IMask.createMask({
        mask: /^(?!.*\.\.)[a-zA-ZА-Яа-яЁё.\d]*$/, // Разрешены буквы, цифры и точка. 2 точки подряд запрещены
      }),
    [],
  );

  const codeElement = React.useMemo(
    () => (
      <div className={styles.inLine}>
        {!routeState?.head && !organization?.head && (
          <SelectField
            label="Тип организационной единицы"
            name={OrganizationCardFormField.ORG_TYPE_CODE}
            items={orgTypesForChildOrg}
            disabled={readOnly}
            size="small"
            required
            allowClear
            groupStyle={readOnly}
          />
        )}
        <TextField
          label="Код организации"
          name={OrganizationCardFormField.CODE}
          disabled={readOnly || !isNew}
          required
          size="small"
          maxLength={10}
          groupStyle={readOnly}
          mask={codeMask}
        />
      </div>
    ),
    [isNew, orgTypesForChildOrg, organization?.head, readOnly, routeState?.head, codeMask],
  );

  const innKppFields = useMemo(
    () => (
      <div className={styles.inLine}>
        <TextField
          label="ИНН"
          name={OrganizationCardFormField.INN}
          disabled
          size="small"
          mask={createMask('ONLY_NUMBERS')}
          maxLength={10}
          groupStyle={readOnly}
        />
        <TextField
          label="КПП"
          name={OrganizationCardFormField.KPP}
          disabled
          size="small"
          maxLength={9}
          mask={createMask('ONLY_NUMBERS')}
          groupStyle={readOnly}
        />
      </div>
    ),
    [readOnly],
  );

  const isHeadOrg = useMemo(
    () => (
      <>
        <SelectField
          label="Краткое наименование организации"
          name={OrganizationCardFormField.COMPANY_CODE}
          items={filteredCompanies}
          disabled={readOnly || organization?.head || organization?.parentCode === null}
          size="small"
          onChange={onChangeCompany}
          required
          groupStyle={readOnly}
        />
        <TextField
          name={OrganizationCardFormField.COMPANY_FULL_NAME}
          label="Полное наименование организации"
          disabled
          size="small"
          maxLength={255}
          groupStyle={readOnly}
        />
      </>
    ),
    [filteredCompanies, onChangeCompany, organization?.head, organization?.parentCode, readOnly],
  );
  const notHeadOrg = useMemo(
    () => (
      <>
        <SelectField
          label="Наименование"
          name={OrganizationCardFormField.COMPANY_CODE}
          items={filteredCompanies}
          disabled={readOnly}
          size="small"
          onChange={onChangeCompany}
          required
          groupStyle={readOnly}
        />
      </>
    ),
    [filteredCompanies, onChangeCompany, readOnly],
  );

  const headOrganization = useMemo(
    () => routeState?.head || organization?.head || organization?.parentCode === null,
    [organization?.head, organization?.parentCode, routeState?.head],
  );

  return (
    <>
      <div className={cm(styles.organizationCardInfo)}>
        <div className={styles.inLine}>{codeElement}</div>

        {routeState?.head || organization?.head || organization?.parentCode === null ? isHeadOrg : notHeadOrg}
        {innKppFields}

        {(!!routeState?.head || organization?.head) && (
          <>
            <TextField
              name={OrganizationCardFormField.ADRESS}
              label="Юридический адрес"
              disabled
              size="small"
              maxLength={255}
              groupStyle={readOnly}
            />
          </>
        )}

        {headOrganization && (
          <SelectField
            label="Бизнес тип организации"
            name={OrganizationCardFormField.BUSINESS_TYPE_CODE}
            items={businessTypeItems}
            disabled={readOnly}
            size="small"
            autoClearSearchValue
            onChange={onChangeBusinessType}
            groupStyle={readOnly}
          />
        )}
      </div>
    </>
  );
}

export const OrganizationCardInfo = observer(OrganizationCardInfoComponent);
